/**
 * Notifications API — durable per-user notification records.
 *
 * Backs the NotificationAdapter in `services/notificationAdapter.ts`. The
 * backend (backend/functions/notifications_api) stores one row per
 * notification under `NOTIF#<user_sub>#<id>` in the tenant partition, so
 * records survive a reload and populate the /notifications history page.
 *
 * Paths are BARE (no version prefix): the dev Vite proxy rewrites `/api` and
 * the backend resolver strips `/systemdox/v1`, so `/notifications` is correct
 * in both environments.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

/** Semantic type — mirrors the shared package's NotificationType. */
export type NotificationRecordType = 'completed' | 'failed' | 'info' | 'warning';

/** One stored notification, as returned by the backend. */
export interface NotificationRecord {
  id: string;
  type: NotificationRecordType;
  title: string;
  description?: string;
  /** Router path such as `/design/doc_123` — validated relative server-side. */
  actionUrl?: string;
  /** Milliseconds since epoch. */
  timestamp: number;
  dismissedAt?: string;
}

/**
 * One page of stored notifications.
 *
 * `nextCursor` is opaque — a server-minted position in the tenant's
 * notification index. Pass it back verbatim to fetch the following page; a
 * null value means the history is exhausted.
 *
 * A page may legitimately hold FEWER than `limit` records while `nextCursor`
 * is still non-null (the server filters dismissed rows after DynamoDB applies
 * its own limit). Callers must therefore decide "is there more?" from
 * `nextCursor`, NEVER from `notifications.length === limit`.
 */
export interface NotificationPage {
  notifications: NotificationRecord[];
  nextCursor: string | null;
}

/**
 * Outcome of a dismiss-all sweep.
 *
 * The server bounds how many rows it will walk in one call, so `complete`
 * false means "dismissed what I could see, more may remain — call again".
 */
export interface DismissAllResult {
  dismissed: number;
  complete: boolean;
}

/** Payload for creating a durable notification. */
export interface CreateNotificationInput {
  type: NotificationRecordType;
  title: string;
  description?: string;
  actionUrl?: string;
  /**
   * The generation this reports on. The server reuses it as the notification
   * id, so the job row, the stored notification and the panel item the user
   * dismisses all share one id.
   */
  jobId?: string;
}

/** An AI job, already mapped to the shared Job shape server-side. */
export interface JobRecord {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  label: string;
  sublabel?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
  meta?: Record<string, unknown>;
}

export const notificationsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /**
   * One page of terminal notifications for the caller, newest first.
   *
   * `cursor` resumes a previous walk. It is opaque and filter-specific: a
   * cursor minted with one `includeDismissed` value must not be replayed with
   * another — restart from page one when the filter changes.
   */
  async list(
    options: { includeDismissed?: boolean; limit?: number; cursor?: string } = {},
  ): Promise<NotificationPage> {
    const { data } = await api.get('/notifications', {
      params: {
        ...(options.includeDismissed ? { includeDismissed: 'true' } : {}),
        ...(options.limit ? { limit: options.limit } : {}),
        ...(options.cursor ? { cursor: options.cursor } : {}),
      },
    });
    return {
      notifications: data.notifications ?? [],
      nextCursor: data.nextCursor ?? null,
    };
  },

  /** In-flight AI jobs started by the caller. */
  async listPending(): Promise<JobRecord[]> {
    const { data } = await api.get('/notifications/pending');
    return data.jobs ?? [];
  },

  /**
   * Status of a single generation, as a whole Job.
   *
   * Not `GET /ai/jobs/<id>`: that returns only `{jobId, status}`, which left
   * the poller no choice but to invent a label and drop the document link on
   * every tick. This route resolves the stored row — the durable notification
   * once the document exists, the in-flight job row before that.
   */
  async getJob(jobId: string): Promise<JobRecord> {
    const { data } = await api.get(`/notifications/jobs/${jobId}`);
    return data;
  },

  async create(input: CreateNotificationInput): Promise<NotificationRecord> {
    const { data } = await api.post('/notifications', input);
    return data;
  },

  async dismiss(id: string): Promise<void> {
    await api.delete(`/notifications/${id}`);
  },

  async dismissAll(): Promise<DismissAllResult> {
    const { data } = await api.delete('/notifications');
    return {
      dismissed: data?.dismissed ?? 0,
      // Absent means an older deployment that always drained what it saw and
      // reported nothing. Treat that as complete: assuming otherwise would
      // send every caller into a pointless retry loop.
      complete: data?.complete !== false,
    };
  },
};
